import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { usePlanning } from "@/components/planning/ForecastCard";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

const PlanCell = ({ c, mo, cell, canEdit, onSave }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(cell.override ?? "");
  const now = new Date().toISOString().slice(0, 7);
  const over = cell.effective && cell.spent > cell.effective;
  const pct = cell.effective ? Math.min(100, (cell.spent / cell.effective) * 100) : 0;
  const save = async () => { setEdit(false); if (String(v) !== String(cell.override ?? "")) { await onSave(c.key, mo, v === "" ? null : Number(v)); } };
  if (edit) return <td className="p-1"><Input type="number" min="1" autoFocus value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") { save(); } }} placeholder={String(cell.base ?? "")} className="h-9 w-[4.75rem] text-xs" data-testid={`plan-input-${c.key}-${mo}`} /></td>;
  return (
    <td className="p-1 align-top">
      <button type="button" disabled={!canEdit} onClick={() => setEdit(true)} title={canEdit ? "Modifier le plafond de ce mois" : undefined}
        className={cn("w-[4.75rem] rounded-lg px-1.5 py-1.5 text-left transition-colors hover:bg-secondary", mo === now && "ring-2 ring-primary/50", cell.override != null && "bg-butter/40")} data-testid={`plan-cell-${c.key}-${mo}`}>
        <div className="num text-[13px] font-bold">{cell.effective != null ? `${Math.round(cell.effective)} €` : "—"}</div>
        {mo <= now ? (
          <>
            <div className="my-1 h-1 overflow-hidden rounded-full bg-secondary"><div className={cn("h-full rounded-full", over ? "bg-destructive" : "bg-success")} style={{ width: `${pct}%` }} /></div>
            <div className={cn("num text-[10px] leading-tight", over ? "font-semibold text-destructive" : "text-muted-foreground")}>{Math.round(cell.spent)} €{over && ` (+${Math.round(cell.spent - cell.effective)})`}</div>
          </>
        ) : cell.carry ? <div className="num mt-1 text-[10px] text-success">+{Math.round(cell.carry)} € reporté</div> : null}
      </button>
    </td>
  );
};

const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground" data-testid="plan-legend">
    <span><b className="text-foreground">450 €</b> = plafond du mois</span>
    <span className="flex items-center gap-1"><i className="h-1 w-5 rounded-full bg-success" />dépensé</span>
    <span className="flex items-center gap-1"><i className="h-1 w-5 rounded-full bg-destructive" />dépassement</span>
    <span className="flex items-center gap-1"><i className="h-3 w-3 rounded bg-butter/60" />plafond modifié</span>
    <span className="flex items-center gap-1"><i className="h-3 w-3 rounded ring-2 ring-primary/50" />mois en cours</span>
  </div>
);

export const AnnualPlan = ({ scope, sid }) => {
  const qc = useQueryClient();
  const [year, setYear] = useState(new Date().getFullYear());
  const { data } = usePlanning(scope, sid, "plan", { year });
  const box = useRef(null);
  useEffect(() => {
    const cell = box.current?.querySelector(`[data-testid$="-${new Date().toISOString().slice(0, 7)}"]`);
    if (cell && box.current && box.current.scrollWidth > box.current.clientWidth) { box.current.scrollLeft += cell.getBoundingClientRect().left - box.current.getBoundingClientRect().left - 330; }
  }, [data]);
  const refresh = () => ["planning", "overview", "budgets", "biz-overview", "biz-dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const saveCell = async (cat, mo, limit) => { try { await api.put(`/planning/${scope}/${sid}/plan/${cat}/${mo}`, { limit }); refresh(); } catch (e) { toast.error(formatApiError(e)); } };
  const setRollover = async (cat, rollover) => { try { await api.put(`/planning/${scope}/${sid}/rollover/${cat}`, { rollover }); refresh(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <section className="card-surface space-y-4 p-5 sm:p-6" data-testid={`annual-plan-${scope}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="tone-chip tone-butter flex h-9 w-9 items-center justify-center rounded-xl"><CalendarRange className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1"><h2 className="text-lg font-bold">Budget prévisionnel annuel</h2>
          <p className="text-xs text-muted-foreground">Touchez un mois pour changer son plafond (vide = plafond habituel). Activez « Report » pour ajouter ce qui n'a pas été dépensé au mois suivant.</p></div>
        <Select value={String(year)} onValueChange={(y) => setYear(Number(y))}><SelectTrigger className="w-full sm:w-28" data-testid={`plan-year-${scope}`}><SelectValue /></SelectTrigger>
          <SelectContent>{[year - 1, year, year + 1].filter((y, i, a) => a.indexOf(y) === i).map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
      </div>
      {!data ? <div className="h-40 animate-pulse rounded-2xl bg-muted" /> : data.categories.length ? (
        <>
        <Legend />
        <div ref={box} className="overflow-x-auto pb-1" data-testid={`plan-table-${scope}`}>
          <table className="border-separate border-spacing-0 text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="sticky left-0 z-10 w-36 min-w-36 bg-card p-1 pr-3 shadow-[6px_0_6px_-6px_rgba(0,0,0,0.15)]">Poste</th>{MONTHS.map((m) => <th key={m} className="p-1 font-medium">{m}</th>)}</tr></thead>
            <tbody>{data.categories.map((c) => (
              <tr key={c.key} data-testid={`plan-row-${c.key}`}>
                <td className="sticky left-0 z-10 w-36 min-w-36 max-w-36 bg-card p-1 pr-3 align-top shadow-[6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                  <div className="truncate pt-1 font-semibold">{c.label}</div>
                  <label className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Switch checked={c.rollover} disabled={!data.can_edit} onCheckedChange={(v) => setRollover(c.key, v)} className="scale-75" aria-label={`Report ${c.label}`} data-testid={`plan-rollover-${c.key}`} />
                    {c.rollover ? "Report activé" : "Report"}
                  </label>
                </td>
                {Object.entries(c.months).map(([mo, cell]) => <PlanCell key={`${mo}-${cell.override}`} c={c} mo={mo} cell={cell} canEdit={data.can_edit} onSave={saveCell} />)}
              </tr>))}</tbody>
          </table>
        </div>
        </>
      ) : <p className="text-sm text-muted-foreground">Définissez d'abord un plafond mensuel par poste.</p>}
      {data && !data.can_edit && <p className="text-xs text-muted-foreground">Consultation seule : les plafonds sont fixés par les responsables.</p>}
    </section>
  );
};
